<script setup>
import { defineProps } from "vue";
import dayjs from "dayjs";
import EquipmentHealthState from "@/components/equipment/EquipmentHealthState.vue";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shadcn/components/ui/dropdown-menu/index.js";
import { Button } from "@/shadcn/components/ui/button/index.js";
import Icon from "../ui/icon/Icon.vue";
import MaintenanceRecordStatusLabel from "@/components/maintenance/MaintenanceRecordStatusLabel.vue";

const props = defineProps({
  maintenanceRecords: {
    type: Array,
    required: true
  },
});

const emit = defineEmits(["refresh", "edit-record"]);

const openModal = (record, shouldEdit) => {
  emit("edit-record", { record, shouldEdit });
};

</script>
<template>
  <div>
    <div class="table-responsive">
      <table class="tw:w-full">
        <thead>
        <tr class="tw:border-t tw:bg-zinc-50">
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Type") }}
          </th>
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Status") }}
          </th>
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Started at") }}
          </th>
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Completed at") }}
          </th>
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Outcome state") }}
          </th>
          <th class="tw:px-4 tw:py-2 tw:text-left tw:font-medium tw:text-sm tw:text-zinc-700">
            {{ $t("Actions") }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="record in maintenanceRecords" :key="record.id"
            class="tw:border-t hover:tw:bg-zinc-50">
          <td class="tw:p-4">
            <div class="tw:flex tw:items-start tw:gap-3">
              <div>
                <div class="tw:font-medium">{{ record.type.label }}</div>
                <template v-if="record.performed_by">
                  <div class="tw:text-sm tw:text-zinc-500 tw:mt-1">
                    {{ $t("Performed by") }}: {{ record.performed_by.firstname }}
                    {{ record.performed_by.lastname }}
                  </div>
                </template>
              </div>
            </div>
          </td>
          <td class="tw:p-4">
            <MaintenanceRecordStatusLabel :status="record.status" />
          </td>
          <td class="tw:p-4 tw:text-sm tw:text-zinc-500">
            <template v-if="record.started_at">
              {{ dayjs(record.started_at).format("lll") }}
            </template>
          </td>
          <td class="tw:p-4 tw:text-sm tw:text-zinc-500">
            <template v-if="record.completed_at">
              {{ dayjs(record.completed_at).format("lll") }}
            </template>
          </td>
          <td class="tw:p-4">
            <template v-if="record.outcome_state?.value">
              <div class="tw:col-span-2 tw:inline-flex tw:items-center tw:gap-1.5 tw:text-zinc-500">
                <EquipmentHealthState :state="record.outcome_state" />
                <span class="tw:block tw:text-sm tw:text-zinc-500">
                {{ record.outcome_state.label }}
              </span>
              </div>
            </template>
          </td>
          <td class="tw:p-4">
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="outline">
                  {{ $t("Actions") }}
                  <Icon name="ChevronDown" class="tw:ml-2 tw:h-4 tw:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <template v-if="record.status?.value !== 'done'">
                  <DropdownMenuItem
                    @click="openModal(record, true)">
                    <Icon name="Pen" class="tw:size-4" />
                    {{ $t("Edit") }}
                  </DropdownMenuItem>
                </template>
                <DropdownMenuItem
                  @click="openModal(record, false)">
                  <Icon name="Eye" class="tw:size-4" />
                  {{ $t("View") }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>